import React, { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import {
  RadioGroup,
  Text,
  Button,
  Drawer,
  Collapse,
  Toasts,
  Clipped,
  MediaQuery,
  Level,
  Box,
  Heading,
  Dialog,
} from '@deseretbook/react-ui'
import { withPopupProps } from './withPopupProps'
import AddressForm from './AddressForm'
import Address from './Address'
import { withCheckout } from './CheckoutContext'
import { Logger, formatErrorMessage } from '../../dbcom'
import { DeseretBook } from '../../dbcom/api'

const logger = new Logger({ context: 'AddressStep' })

/**
 * Address step of checkout.
 */
class AddressStep extends Component {
  /**
   * Component constructor.
   */
  constructor() {
    super()

    this.state = {
      // We lazy load the address form since it has to make a bunch of data requests.
      newAddressFormIsRendered: false,
      // This is the actively selected address, if they submit the step, this is
      // what will be saved.
      selectedAddress: {},
      // This is the actual address that is selected on the order. It's the fallback
      // in case they cancel any changes.
      propsSelectedAddress: {},
      form: {},
      // If the create address form is open or not.
      isCreating: false,
    }

    this.handleFormChange = this.handleFormChange.bind(this)
    this.openDrawer = this.openDrawer.bind(this)
    this.closeDrawer = this.closeDrawer.bind(this)
    this.showCreateForm = this.showCreateForm.bind(this)
    this.selectNewAddress = this.selectNewAddress.bind(this)
    this.verifyAddress = this.verifyAddress.bind(this)
    this.cancelAddressVerification = this.cancelAddressVerification.bind(this)
    this.saveStep = this.saveStep.bind(this)
    this.clearForm = this.clearForm.bind(this)
    this.createNewAddress = this.createNewAddress.bind(this)
    this.renderAddressField = this.renderAddressField.bind(this)
    this.renderMobile = this.renderMobile.bind(this)
    this.renderDesktop = this.renderDesktop.bind(this)
  }

  /**
   * Figures out what should change in the components state based on props.
   *
   * @param {object} props - Component props
   * @param {object} state - Component state
   * @returns {object} Changes to state
   */
  static getDerivedStateFromProps(props, state) {
    const { order, isActive, popup } = props
    const { propsSelectedAddress, propsIsActive } = state

    if (isActive !== propsIsActive) {
      popup.close()

      return {
        selectedAddress: order.shipAddress || {},
        propsSelectedAddress: order.shipAddress || {},
        propsIsActive: isActive,
        form: {
          countryId: order.shipAddress && order.shipAddress.countryId,
          stateId: order.shipAddress && order.shipAddress.stateId,
        },
        isCreating: false,
      }
    }

    if (
      order &&
      order.shipAddress &&
      (!propsSelectedAddress ||
        order.shipAddress.id !== propsSelectedAddress.id)
    ) {
      return {
        selectedAddress: order.shipAddress || {},
        propsSelectedAddress: order.shipAddress || {},
      }
    }

    return null
  }

  /**
   * Initializes step once the component is mounted.
   */
  componentDidMount() {
    const { order, user } = this.props

    // TODO determine if this should be in componentDidUpdate?
    if (!order.shipAddressId || (user && !user.addresses)) {
      this.showCreateForm()
    }
  }

  /**
   * Form input change handler. Applies given changes to the address form.
   *
   * @param {object} [changes={}] - Changes to form values.
   */
  handleFormChange(changes = {}) {
    this.setState(({ form }) => ({
      form: {
        ...form,
        ...changes,
      },
    }))
  }

  /**
   * Resets the form and clears form related state back to its original values.
   */
  clearForm() {
    const { popup } = this.props

    // CLoses the drawer
    popup.close()
    this.setState(({ selectedAddress }) => ({
      isCreating: false,
      form: {
        countryId: selectedAddress && selectedAddress.countryId,
        stateId: selectedAddress && selectedAddress.stateId,
      },
    }))
  }

  /**
   * Opens the mobile version of the new address form.
   */
  openDrawer() {
    const { popup } = this.props

    // Opens the drawer
    popup.open()
    this.setState(() => ({
      isCreating: true,
      newAddressFormIsRendered: true,
    }))
  }

  /**
   * Closes the mobile version of the new address form.
   */
  closeDrawer() {
    const { popup } = this.props

    // Closes the drawer
    popup.close()
    this.setState(({ selectedAddress }) => ({
      isCreating: false,
      form: {
        countryId: selectedAddress && selectedAddress.countryId,
        stateId: selectedAddress && selectedAddress.stateId,
      },
    }))

    this.clearForm()
  }

  /**
   * Changes the steps state to create mode.
   */
  showCreateForm() {
    this.setState({
      isCreating: true,
      newAddressFormIsRendered: true,
    })
  }

  /**
   * On change event handler for when users want to select an address from their address book.
   * @param {event} event - on change event
   */
  selectNewAddress({ target }) {
    const { value } = target
    const { user } = this.props

    if (!user) {
      return
    }

    const address = user.addresses.filter(({ id }) => id === +value)[0] || {}

    this.setState({ selectedAddress: address })
  }

  /**
   * Form submit handler for when the user creates a new address. Updates the selected address
   * to the current form values and then attempts to save the step.
   */
  async createNewAddress() {
    this.setState(() => ({ isLoading: true }))

    const { form } = this.state

    this.setState({ selectedAddress: form }, () => {
      this.saveStep()
    })
  }

  /**
   * Closes the address verification dialog, allowing users to edit their address before saving it.
   */
  cancelAddressVerification() {
    this.setState({ confirmAddress: false })
  }

  /**
   * Attempts to verify the address. This is essentially the same as resubmitting the step but its
   * how we allow the user to verify that the address they entered is the one they want.
   */
  verifyAddress() {
    const { order, updateOrder, startLoading, stopLoading } = this.props

    startLoading()

    this.setState({ isLoading: true }, async () => {
      try {
        const updated = await DeseretBook.checkout().next(order)

        updateOrder(updated, true)
        this.setState({ isLoading: false, confirmAddress: false })
        Toasts.success('Address verified')
      } catch ({ message, errors }) {
        logger.error(
          `Unable to verify address: "${formatErrorMessage(message, errors)}"`,
          { errors },
        )
        Toasts.danger(
          'Unable to verify address',
          formatErrorMessage(message, errors),
        )
        this.setState({ isLoading: false })
        stopLoading()
      }
    })
  }

  /**
   * Attempts to save the address step and move the checkout process along.
   */
  saveStep() {
    const { order, updateOrder, startLoading, stopLoading } = this.props

    startLoading()

    this.setState({ isLoading: true }, async () => {
      const { selectedAddress } = this.state

      // Solidus doesn't like us sending the actual state/country objects so we remove them.
      const shipAddressAttributes = {
        ...selectedAddress,
        state: null,
        country: null,
      }

      // If there isn't a billing address yet, we need to fake it so we can continue on.
      const billAddressAttributes = !order.billAddressId
        ? shipAddressAttributes
        : null

      try {
        const { popup } = this.props
        const updatedOrder = await DeseretBook.checkout(order).update({
          order: {
            state: 'address',
            shipAddressAttributes,
            billAddressAttributes,
          },
        })

        // Let the rest of the page know the order was updated.
        updateOrder(updatedOrder, true)

        Toasts.success('Delivery address saved')

        // Closes the drawer
        popup.close()

        this.setState({
          isLoading: false,
          form: {
            countryId: selectedAddress.countryId,
            stateId: selectedAddress.stateId,
          },
          isCreating: false,
        })
      } catch ({ message, errors }) {
        if (errors && errors.base && errors.base.length) {
          this.setState({
            confirmAddress: true,
            isLoading: false,
          })

          logger.info('User entered a risky address', { message, errors })
        } else {
          logger.error(
            `Unable to save address step: "${formatErrorMessage(
              message,
              errors,
            )}"`,
            { errors },
          )
          Toasts.danger('Unable to save', formatErrorMessage(message, errors))
          this.setState({ isLoading: false })
        }

        stopLoading()
      }
    })
  }

  /**
   * Render helper. Determines how the address field portion of the address step should be rendered.
   *
   * @returns {function} Component
   */
  renderAddressField() {
    const { user } = this.props
    const { isLoading, selectedAddress } = this.state
    const addresses = (user && user.addresses) || []
    const selectedAddressInAddressBook = !!addresses.filter(
      ({ id }) => id === selectedAddress.id,
    ).length

    if (!addresses.length) {
      return false
    }

    return (
      <Fragment>
        {!selectedAddressInAddressBook && (
          <Box paddingBottom={1}>
            <Heading>Selected Address</Heading>
            <Address address={selectedAddress} />
          </Box>
        )}
        {!!addresses.length && (
          <Fragment>
            <Heading>My Saved Addresses</Heading>
            <RadioGroup
              name="id"
              aria-label="Shipping Address"
              selectedValue={selectedAddress.id}
              onChange={this.selectNewAddress}
              disabled={isLoading}
            >
              {addresses.map(address => {
                const formattedAddress = [
                  address.address1,
                  address.address2,
                  address.address3,
                  address.city,
                  address.state ? address.state.name : address.stateName,
                  address.zipcode,
                  address.country ? address.country.iso : null,
                ]
                  .filter(Boolean)
                  .join(', ')

                return (
                  <RadioGroup.Option value={address.id} key={address.id}>
                    <Clipped>
                      <Text isBold hasEllipsis>
                        {address.firstname} {address.lastname}
                      </Text>
                      <Text hasEllipsis>{formattedAddress}</Text>
                    </Clipped>
                  </RadioGroup.Option>
                )
              })}
            </RadioGroup>
          </Fragment>
        )}
      </Fragment>
    )
  }

  /**
   * Render helper. Determines how to render the mobile version of the address step.
   *
   * @returns {function} Component
   */
  renderMobile() {
    const { user, order, popup } = this.props
    const { newAddressFormIsRendered, isLoading, form } = this.state

    const isCancelable =
      order.state !== 'address' ||
      (user && user.addresses && user.addresses.length)

    return (
      <Box paddingBottom={2}>
        {this.renderAddressField()}
        <Button
          isFullwidth
          disabled={isLoading}
          type="button"
          {...popup.triggerProps}
          onClick={this.openDrawer}
        >
          Add New Address
        </Button>
        <Drawer {...popup.popupProps} close={this.closeDrawer}>
          <Drawer.Header>
            <Text isBold hasEllipsis>
              New Address
            </Text>
          </Drawer.Header>
          <Drawer.Content>
            {!!newAddressFormIsRendered && (
              <AddressForm
                address={form}
                onSubmit={this.createNewAddress}
                onChange={this.handleFormChange}
                onCancel={isCancelable ? this.clearForm : null}
                isSubmitting={isLoading}
              />
            )}
          </Drawer.Content>
        </Drawer>
      </Box>
    )
  }

  /**
   * Render helper. Determines how the desktop version of the address step should be rendered.
   *
   * @returns {function} Component
   */
  renderDesktop() {
    const { user, order } = this.props
    const { isCreating, newAddressFormIsRendered, isLoading, form } = this.state

    const canViewAddressList =
      isCreating && ((user && user.addresses.length) || order.shipAddressId)

    return (
      <div>
        <Collapse isCollapsed={isCreating}>
          {this.renderAddressField()}
        </Collapse>
        <Collapse isCollapsed={!isCreating}>
          {newAddressFormIsRendered ? (
            <AddressForm
              address={form}
              onSubmit={this.createNewAddress}
              onChange={this.handleFormChange}
              onCancel={canViewAddressList ? this.clearForm : null}
              isSubmitting={isLoading}
            />
          ) : (
            <div />
          ) // Collapse doesn't like not having children
          }
        </Collapse>
      </div>
    )
  }

  /**
   * Determines how the component should be rendered.
   *
   * @returns {function} Component
   */
  render() {
    const { order, onCloseStep } = this.props
    const {
      isLoading,
      isCreating,
      confirmAddress,
      selectedAddress,
    } = this.state

    return (
      <MediaQuery.Medium>
        {matches => (
          <React.Fragment>
            {!!confirmAddress && (
              <Dialog
                isOpen={confirmAddress}
                isConfirming={isLoading}
                onConfirm={this.verifyAddress}
                onCancel={this.cancelAddressVerification}
                confirmButtonText="Verify Address"
                cancelButtonText="Make Changes"
                style={{ zIndex: 1000000000 }}
              >
                <Text size="large" hasEllipsis>
                  Confirm your address
                </Text>
                <Text display="block">
                  We were unable to verify your address. Please confirm the
                  address is entered correctly, or make changes to it before
                  continuing.
                </Text>
                <Box paddingVertical={1}>
                  <Address address={selectedAddress} />
                </Box>
              </Dialog>
            )}
            {matches ? this.renderDesktop() : this.renderMobile()}
            {!isCreating && (
              <Level>
                {!!matches && (
                  <Level.Left>
                    <Level.Item>
                      <Button
                        type="button"
                        onClick={this.showCreateForm}
                        disabled={isLoading}
                      >
                        Add New Address
                      </Button>
                    </Level.Item>
                  </Level.Left>
                )}
                <Level.Right>
                  {order.state !== 'address' && (
                    <Level.Item>
                      <Button
                        type="button"
                        onClick={onCloseStep}
                        disabled={isLoading}
                      >
                        Cancel
                      </Button>
                    </Level.Item>
                  )}
                  <Level.Item>
                    <Button
                      type="button"
                      color="action"
                      onClick={this.saveStep}
                      isLoading={isLoading}
                    >
                      {order.state === 'address' ? 'Continue' : 'Save Changes'}
                    </Button>
                  </Level.Item>
                </Level.Right>
              </Level>
            )}
          </React.Fragment>
        )}
      </MediaQuery.Medium>
    )
  }
}

AddressStep.propTypes = {
  order: PropTypes.shape({
    state: PropTypes.string,
    shipAddress: PropTypes.shape({}),
    shipAddressId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    billAddressId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired, // checkout context
  updateOrder: PropTypes.func.isRequired, // checkout context
  startLoading: PropTypes.func.isRequired, // checkout context
  stopLoading: PropTypes.func.isRequired, // checkout context
  // Linter can't see static methods, so it things this prop is never used.
  /* eslint-disable-next-line react/no-unused-prop-types */
  isActive: PropTypes.bool.isRequired,
  user: PropTypes.shape({}), // checkout context
  onCloseStep: PropTypes.func,
  popup: PropTypes.shape({
    triggerProps: PropTypes.object,
    popupProps: PropTypes.object,
    close: PropTypes.func,
    open: PropTypes.func,
    isOpen: PropTypes.bool,
  }),
}

AddressStep.defaultProps = {
  user: undefined,
  onCloseStep: () => {},
}

export default withCheckout(withPopupProps(AddressStep))
